import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faRotate, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useStaffEmails } from "../store/staff";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const STATUSES = ["PENDING", "PROCESSING", "RETRYING", "SENT", "FAILED"];
// The list filter only accepts these two job kinds (responses may contain others).
const EMAIL_TYPES = ["WAEC_PIN_FULFILLMENT", "WAEC_PIN_NO_INVENTORY"];

const fmtDate = (value) => (value ? new Date(value).toLocaleString() : "—");
const badge = (value) =>
  value ? (
    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
      {value}
    </span>
  ) : (
    "—"
  );

const StaffEmails = () => {
  const {
    staffEmails,
    staffEmailsLoading,
    staffEmailsError,
    staffEmailsTotal,
    staffEmailsPages,
    staffFailedEmails,
    staffFailedEmailsLoading,
    staffFailedEmailsError,
    staffFailedEmailsTotal,
    staffFailedEmailsPages,
    staffEmailDetail,
    staffEmailDetailLoading,
    staffEmailMutationLoading,
    staffEmailMutationError,
    fetchStaffEmails,
    fetchStaffEmail,
    fetchStaffFailedEmails,
    retryStaffEmail,
  } = useStaffEmails();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [emailType, setEmailType] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const params = {
    page,
    limit: 10,
    ...(status ? { status } : {}),
    ...(emailType ? { emailType } : {}),
  };

  // GET /api/staff/emails  OR  GET /api/staff/emails/failed
  useEffect(() => {
    if (failedOnly) {
      fetchStaffFailedEmails({ page, limit: 10 });
    } else {
      fetchStaffEmails(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, emailType, failedOnly]);

  const rows = failedOnly ? staffFailedEmails : staffEmails;
  const loading = failedOnly ? staffFailedEmailsLoading : staffEmailsLoading;
  const error = failedOnly ? staffFailedEmailsError : staffEmailsError;
  const total = failedOnly ? staffFailedEmailsTotal : staffEmailsTotal;
  const pages = failedOnly ? staffFailedEmailsPages : staffEmailsPages;

  const reload = () => {
    if (failedOnly) fetchStaffFailedEmails({ page, limit: 10 });
    else fetchStaffEmails(params);
  };

  const canRetry = (job) => job?.status === "FAILED" || job?.status === "RETRYING";

  const detail = staffEmailDetail || {};
  const attempts = Array.isArray(detail.attemptRecords) ? detail.attemptRecords : [];

  return (
    <div>
      <StaffNavBar
        title="Email Deliveries"
        context="Monitor PIN delivery emails and retry failures."
      />
      <AdminPageBody>
        <AdminPanel className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
            <select
              className={`${inputCls} max-w-[190px]`}
              value={status}
              disabled={failedOnly}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              className={`${inputCls} max-w-[240px]`}
              value={emailType}
              disabled={failedOnly}
              onChange={(e) => {
                setPage(1);
                setEmailType(e.target.value);
              }}
            >
              <option value="">All email types</option>
              {EMAIL_TYPES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={failedOnly}
                onChange={(e) => {
                  setPage(1);
                  setFailedOnly(e.target.checked);
                }}
              />
              Failed / retrying only
            </label>
            <button className={`${adminButton} ml-auto`} onClick={reload}>
              <FontAwesomeIcon icon={faRotate} /> Refresh
            </button>
          </div>

          {staffEmailMutationError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {staffEmailMutationError}
            </div>
          )}
          {error && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <AdminLoadingState />
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              No email jobs found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Type</th>
                    <th className="px-5 py-3 font-semibold">Recipient</th>
                    <th className="px-5 py-3 font-semibold">Order</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 text-right font-semibold">Attempts</th>
                    <th className="px-5 py-3 font-semibold">Updated</th>
                    <th className="px-5 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-slate-800">{e.emailType || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{e.recipient || "—"}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-600">
                        {e.order?.reference || "—"}
                      </td>
                      <td className="px-5 py-3">{badge(e.status)}</td>
                      <td className="px-5 py-3 text-right text-slate-600">
                        {e.attempts ?? "—"}
                        {e._count?.attemptRecords != null && (
                          <div className="text-[11px] text-slate-400">
                            {e._count.attemptRecords} records
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {fmtDate(e.updatedAt)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className={adminButton}
                            onClick={() => {
                              setDetailId(e.id);
                              fetchStaffEmail(e.id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className={adminButton}
                            title="Retry delivery"
                            disabled={staffEmailMutationLoading || !canRetry(e)}
                            onClick={async () => {
                              const res = await retryStaffEmail(e.id);
                              if (res.success) reload();
                            }}
                          >
                            <FontAwesomeIcon icon={faRotate} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
            <span>
              Page {page} of {pages || 1} · {total || 0} jobs
            </span>
            <div className="flex gap-2">
              <button className={adminButton} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </button>
              <button
                className={adminButton}
                disabled={page >= (pages || 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </AdminPanel>
      </AdminPageBody>

      {detailId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-auto w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="m-0 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FontAwesomeIcon icon={faEnvelope} /> Email Job
              </h3>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDetailId(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {staffEmailDetailLoading ? (
              <AdminLoadingState />
            ) : (
              <div className="space-y-5">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {[
                    ["Type", detail.emailType],
                    ["Recipient", detail.recipient],
                    ["Subject", detail.subject],
                    ["Provider", detail.provider],
                    ["Status", detail.status],
                    ["Attempts", detail.attempts],
                    ["Order", detail.order?.reference],
                    ["Provider message id", detail.providerMessageId],
                    ["Created", fmtDate(detail.createdAt)],
                    ["Updated", fmtDate(detail.updatedAt)],
                    ["Sent", fmtDate(detail.sentAt)],
                    ["Failed", fmtDate(detail.failedAt)],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {label}
                      </dt>
                      <dd className="m-0 break-words text-sm text-slate-800">
                        {value != null && value !== "" ? String(value) : "—"}
                      </dd>
                    </div>
                  ))}
                </dl>

                {detail.lastError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {detail.lastError}
                  </div>
                )}

                <section>
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">
                    Attempt history ({detail._count?.attemptRecords ?? attempts.length})
                  </h4>
                  {attempts.length === 0 ? (
                    <p className="text-sm text-slate-400">No attempts recorded.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {attempts.map((attempt) => (
                        <li key={attempt.id} className="px-4 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium text-slate-800">
                              {attempt.status || "—"} · {attempt.provider || "—"}
                            </span>
                            <span className="text-xs text-slate-400">
                              {fmtDate(attempt.createdAt)}
                            </span>
                          </div>
                          {(attempt.errorKind || attempt.errorMessage) && (
                            <div className="text-xs text-red-700">
                              {[attempt.errorKind, attempt.errorMessage]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )}
                          {attempt.messageId && (
                            <div className="break-all text-[11px] text-slate-400">
                              {attempt.messageId}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                className={adminButton}
                disabled={staffEmailMutationLoading || !canRetry(detail)}
                onClick={async () => {
                  const res = await retryStaffEmail(detailId);
                  if (res.success) {
                    await fetchStaffEmail(detailId);
                    reload();
                  }
                }}
              >
                <FontAwesomeIcon icon={faRotate} /> Retry
              </button>
              <button className={adminPrimaryButton} onClick={() => setDetailId(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffEmails;
